<script lang="ts">
  import {
    eventMoveConfirmOpen,
    pendingEventMove,
    confirmPendingEventMove,
    cancelPendingEventMove,
  } from '../lib/stores/ui';
  import {
    eventTotalDays,
    getWeekStart,
    getWeekEnd,
    MS_PER_DAY,
    parseCalendarDate,
    formatCalendarDate,
  } from '../lib/utils';
  import { useModalAnimation } from '../lib/useModalAnimation.svelte';

  const modal = useModalAnimation(() => $eventMoveConfirmOpen, cancelPendingEventMove);

  function handleConfirm() {
    confirmPendingEventMove();
  }

  function handleKeydown(e: KeyboardEvent) {
    if (!$eventMoveConfirmOpen) return;
    if (e.key === 'Escape') modal.animateClose();
    if (e.key === 'Enter') handleConfirm();
  }

  function formatDate(dateStr: string): string {
    const d = parseCalendarDate(dateStr);
    return formatCalendarDate(d, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  let moveDescription = $derived.by(() => {
    const move = $pendingEventMove;
    if (!move) return null;

    const event = move.event;
    const totalDays = eventTotalDays(event);
    const currentStart = event.date;
    const currentEnd = event.endDate || event.date;

    // Compute new start date based on move type
    let newStartStr: string;
    if (move.moveType === 'day' && move.date) {
      newStartStr = move.date;
    } else if (move.moveType === 'week' && move.weekKey) {
      // Move to Monday of target week
      const ws = getWeekStart(move.weekKey);
      // getWeekStart returns Sunday, Monday is +1
      const monday = new Date(ws.getTime() + MS_PER_DAY);
      newStartStr = monday.toISOString().slice(0, 10);
    } else if (move.moveType === 'month' && move.monthKey) {
      newStartStr = `${move.monthKey}-01`;
    } else {
      return null;
    }

    // Compute new end date preserving duration
    const durationMs = (totalDays - 1) * MS_PER_DAY;
    const newEndDate = new Date(parseCalendarDate(newStartStr).getTime() + durationMs);
    const newEndStr = newEndDate.toISOString().slice(0, 10);

    // Compute shift in days
    const shiftMs =
      parseCalendarDate(newStartStr).getTime() - parseCalendarDate(currentStart).getTime();
    const shiftDays = Math.round(shiftMs / MS_PER_DAY);

    return {
      currentStart: formatDate(currentStart),
      currentEnd: formatDate(currentEnd),
      newStart: formatDate(newStartStr),
      newEnd: formatDate(newEndStr),
      totalDays,
      shiftDays,
    };
  });
</script>

<svelte:window onkeydown={handleKeydown} />

{#if $eventMoveConfirmOpen && $pendingEventMove && moveDescription}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="modal-overlay"
    class:closing={modal.closing}
    bind:this={modal.overlayEl}
    onclick={modal.animateClose}
    role="presentation"
  >
    <div class="modal-card" onclick={(e) => e.stopPropagation()} role="dialog" tabindex="-1">
      <div class="modal-header">
        <div class="modal-icon">↗</div>
        <h2 class="modal-title">
          {moveDescription.totalDays > 1 ? 'Move Multi-Day Event' : 'Move Event'}
        </h2>
      </div>

      <div class="modal-body">
        <p class="event-name">{$pendingEventMove.event.title}</p>
        <p class="event-duration">
          {#if moveDescription.totalDays > 1}
            This event spans <strong>{moveDescription.totalDays} days</strong>.
            {#if moveDescription.shiftDays !== 0}
              Moving it will shift the entire date range
              <span class="shift-amount {moveDescription.shiftDays > 0 ? 'forward' : 'back'}">
                {Math.abs(moveDescription.shiftDays)}
                {Math.abs(moveDescription.shiftDays) === 1 ? 'day' : 'days'}
                {moveDescription.shiftDays > 0 ? 'forward' : 'back'}
              </span>.
            {:else}
              The date range will not change.
            {/if}
          {:else}
            This event spans <strong>1 day</strong>. Confirm moving it to the selected date.
          {/if}
        </p>

        <div class="date-change">
          <div class="date-row">
            <span class="date-label">Current</span>
            <span class="date-value">
              {moveDescription.currentStart} — {moveDescription.currentEnd}
            </span>
          </div>
          <div class="date-arrow">↓</div>
          <div class="date-row new">
            <span class="date-label">New</span>
            <span class="date-value">{moveDescription.newStart} — {moveDescription.newEnd}</span>
          </div>
        </div>
      </div>

      <div class="modal-actions">
        <button class="btn btn-cancel" onclick={modal.animateClose}>Cancel</button>
        <button class="btn btn-confirm" onclick={handleConfirm}>Move Event</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 60;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    animation: overlay-in 150ms ease-out both;
  }

  .modal-overlay.closing {
    animation: overlay-out 100ms ease-in both;
  }

  .modal-overlay.closing .modal-card {
    animation: card-out 100ms ease-in both;
  }

  .modal-card {
    background: white;
    border-radius: 0.75rem;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 8px 10px -6px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 24rem;
    margin: 0 1rem;
    overflow: hidden;
    animation: card-in 150ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  .modal-header {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 1.25rem 1.25rem 0;
  }

  .modal-icon {
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #dbeafe;
    color: #1d4ed8;
    border-radius: 0.5rem;
    font-size: 1rem;
    flex-shrink: 0;
  }

  .modal-title {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .modal-body {
    padding: 1rem 1.25rem;
  }

  .event-name {
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.375rem;
  }

  .event-duration {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0 0 1rem;
    line-height: 1.4;
  }

  .shift-amount {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .shift-amount.forward {
    color: #0369a1;
  }

  .shift-amount.back {
    color: #9333ea;
  }

  .date-change {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.75rem;
  }

  .date-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .date-row.new {
    color: #1d4ed8;
    font-weight: 500;
  }

  .date-label {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #9ca3af;
    width: 3.5rem;
    flex-shrink: 0;
  }

  .date-row.new .date-label {
    color: #3b82f6;
  }

  .date-value {
    font-size: 0.8125rem;
    font-family: ui-monospace, monospace;
  }

  .date-arrow {
    text-align: center;
    color: #9ca3af;
    font-size: 0.75rem;
    padding: 0.25rem 0;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 0 1.25rem 1.25rem;
  }

  .btn {
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 150ms;
    border: none;
  }

  .btn-cancel {
    background: white;
    color: #4b5563;
    border: 1px solid #d1d5db;
  }

  .btn-cancel:hover {
    background: #f3f4f6;
  }

  .btn-confirm {
    background: #2563eb;
    color: white;
  }

  .btn-confirm:hover {
    background: #1d4ed8;
  }

  @keyframes overlay-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes overlay-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes card-in {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes card-out {
    from {
      transform: scale(1);
      opacity: 1;
    }
    to {
      transform: scale(0.95);
      opacity: 0;
    }
  }
</style>
